import cn from "classnames";
import { useEffect } from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import {
	ContentType,
	findSupportedFile,
	MEDIA_MAX_BYTES,
	YOUTUBE_MAX_VIDEO_BYTES,
} from "shared";
import { useEventCallback } from "usehooks-ts";
import { Dialog } from "../common/dialog/Dialog";
import { Progress } from "../common/progress/Progress";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Button } from "../components/Button";
import { ButtonIcon } from "../components/ButtonIcon";
import { StatusPlaceholder } from "../components/StatusPlaceholder";
import { useMediaFolders } from "../content/useMediaFolders";
import { numToFixed } from "../helpers/numbers";
import { Svg } from "../svg";
import styles from "./MediaUploadDialog.module.css";
import {
	type Upload,
	useSetUploadFolder,
	useUpload,
	useUploadList,
} from "./upload";
import { validateYouTubeShortsFileBeforeUpload } from "./validateYouTubeShortsUpload";

export type MediaUploadMode = "default" | "youtubeShorts" | "videoOnly";

export type MediaUploadDialogProps = {
	mode?: MediaUploadMode;
};

const DEFAULT_MAX_MB = Math.round(MEDIA_MAX_BYTES / (1024 * 1024));
const SHORTS_MAX_MB = Math.round(YOUTUBE_MAX_VIDEO_BYTES / (1024 * 1024));

type DropHandlers = {
	addFile: ReturnType<typeof useUploadList>["addFile"];
	addError: ReturnType<typeof useUploadList>["addError"];
};

function fileExtLabel(file: File): string {
	const extension = file.name.split(".").pop()?.toLowerCase();
	return extension ? ` (.${extension})` : "";
}

function rejectedTypeMessage(file: File, mode: MediaUploadMode): string {
	const extLabel = fileExtLabel(file);
	if (mode === "youtubeShorts") {
		return `Unsupported file type${extLabel}. YouTube Shorts only accept MP4 or MOV.`;
	}
	if (mode === "videoOnly") {
		return `Unsupported file type${extLabel}. Use MP4, MOV, or WEBM.`;
	}
	return `Unsupported file type${extLabel}.`;
}

export function createOnDrop(
	{ addFile, addError }: DropHandlers,
	mode: MediaUploadMode = "default",
) {
	return async (files: File[]) => {
		for (const file of files) {
			if (mode === "youtubeShorts") {
				const result = await validateYouTubeShortsFileBeforeUpload(file);
				if (!result.valid) {
					addError(file, result.errors.join(" "));
					continue;
				}
				addFile(file);
				continue;
			}

			if (file.size > MEDIA_MAX_BYTES) {
				addError(
					file,
					`File is larger than ${DEFAULT_MAX_MB}MB. Maximum upload size is ${DEFAULT_MAX_MB}MB.`,
				);
				continue;
			}

			const extension = file.name.split(".").pop()?.toLowerCase();
			const meta = findSupportedFile(file.type, extension);

			if (!meta) {
				const extLabel = extension ? ` (.${extension})` : "";
				addError(
					file,
					`Unsupported file type${extLabel}. Use JPG, PNG, WEBP, MP4, MOV, WEBM, AVI, or WMV.`,
				);
				continue;
			}

			if (mode === "videoOnly" && meta.type !== ContentType.VIDEO) {
				addError(
					file,
					"TikTok ads need a video (MP4, MOV, or WEBM), not an image.",
				);
				continue;
			}

			addFile(file);
		}
	};
}

/** Surface accept-filter rejections (wrong format) that never reach onDrop. */
export function createOnDropRejected(
	{ addError }: Pick<DropHandlers, "addError">,
	mode: MediaUploadMode = "default",
) {
	return (rejections: FileRejection[]) => {
		for (const { file, errors } of rejections) {
			const isTypeError = errors.some((e) => e.code === "file-invalid-type");
			if (isTypeError) {
				addError(file, rejectedTypeMessage(file, mode));
				continue;
			}
			const fallback =
				errors
					.map((e) => e.message)
					.filter(Boolean)
					.join(" ") || `Could not upload${fileExtLabel(file)}.`;
			addError(file, fallback);
		}
	};
}

/** @deprecated Prefer createOnDrop — kept for call sites that need a sync wrapper name. */
export function onDrop(
	addFile: ReturnType<typeof useUploadList>["addFile"],
	addError: ReturnType<typeof useUploadList>["addError"],
	mode: MediaUploadMode = "default",
) {
	return createOnDrop({ addFile, addError }, mode);
}

function FolderPicker() {
	const { data: folders = [] } = useMediaFolders();
	const setUploadFolder = useSetUploadFolder();

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const val = e.target.value;
		setUploadFolder(val === "" ? null : Number(val));
	};

	return (
		<ExtraForm.Group label="Upload to folder" className="mb-3">
			<ExtraForm.Select onChange={handleChange} defaultValue="">
				<option value="">Unfiled</option>
				{folders
					.filter((f) => !f.isArchive)
					.map((f) => (
						<option key={f.id} value={f.id}>
							{f.name}
						</option>
					))}
			</ExtraForm.Select>
		</ExtraForm.Group>
	);
}

export function MediaUploadDialog({
	mode = "default",
}: MediaUploadDialogProps) {
	const { ids, addFile, removeFile, addError } = useUploadList();
	const isShorts = mode === "youtubeShorts";
	const isVideoOnly = mode === "videoOnly";
	const singleFile = isShorts || isVideoOnly;

	const handleDrop = useEventCallback(async (files: File[]) => {
		if (singleFile && files.length > 0) {
			for (const id of ids) {
				removeFile(id);
			}
		}
		await createOnDrop({ addFile, addError }, mode)(files);
	});

	const handleDropRejected = useEventCallback((rejections: FileRejection[]) => {
		if (singleFile && rejections.length > 0) {
			for (const id of ids) {
				removeFile(id);
			}
		}
		createOnDropRejected({ addError }, mode)(rejections);
	});

	const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
		onDrop: handleDrop,
		onDropRejected: handleDropRejected,
		noClick: true,
		noKeyboard: true,
		multiple: !singleFile,
		accept: isShorts
			? {
					"video/mp4": [".mp4"],
					"video/quicktime": [".mov"],
				}
			: isVideoOnly
				? {
						"video/mp4": [".mp4"],
						"video/quicktime": [".mov"],
						"video/webm": [".webm"],
					}
				: undefined,
	});

	const onDialogClose = useEventCallback(() => {
		for (const id of ids) {
			removeFile(id);
		}
	});

	useEffect(() => {
		return () => onDialogClose();
	}, [onDialogClose]);

	const title = isShorts
		? "Upload YouTube Short"
		: isVideoOnly
			? "Upload video"
			: "Upload media";
	const sublabel = isShorts
		? `Drag & drop or browse. MP4 or MOV, vertical 9:16, max 60s, max ${SHORTS_MAX_MB}MB`
		: isVideoOnly
			? `Drag & drop or browse. MP4, MOV, or WEBM. Max ${DEFAULT_MAX_MB}MB`
			: `Drag & drop or browse. Max ${DEFAULT_MAX_MB}MB`;

	return (
		<Dialog {...getRootProps()}>
			<Dialog.Header title={title} />
			<Dialog.Body>
				{isDragActive && (
					<div className={styles.uploadDrop}>Drop files here</div>
				)}
				<input {...getInputProps()} />
				{!isShorts && <FolderPicker />}
				{ids.length === 0 && (
					<StatusPlaceholder
						label={title}
						sublabel={sublabel}
						image={Svg.Uploading}
					>
						<Button onClick={open}>Browse files</Button>
					</StatusPlaceholder>
				)}

				<div className={styles.uploadList}>
					{ids.map((id) => (
						<UploadEntry key={id} ID={id} />
					))}
				</div>
				{ids.length > 0 && !singleFile && (
					<div className={styles.uploadMore}>
						<Button size="sm" onClick={open} icon="add_circle">
							Select more
						</Button>
					</div>
				)}
				{ids.length > 0 && singleFile && (
					<div className={styles.uploadMore}>
						<Button size="sm" onClick={open}>
							Replace file
						</Button>
					</div>
				)}
			</Dialog.Body>
		</Dialog>
	);
}

export function UploadPreview({ file }: { file: File }) {
	if (file.type.startsWith("image/")) {
		return (
			<img
				draggable={false}
				className={styles.uploadPreview}
				src={URL.createObjectURL(file)}
				alt={file.name}
			/>
		);
	}

	return (
		<video
			tabIndex={-1}
			className={styles.uploadPreview}
			muted
			autoPlay={false}
			controls={false}
		>
			<source src={URL.createObjectURL(file)} />
		</video>
	);
}

export function UploadEntry(props: { ID: string }) {
	const upload = useUpload(props.ID);
	const { removeFile } = useUploadList();
	const error = upload.state.status === "ERROR";

	return (
		<div className={styles.upload}>
			<UploadPreview file={upload.file} />
			<div className={styles.uploadDetails}>
				<div className={styles.uploadRight}>
					<div className={styles.uploadName}>
						<div>{upload.file.name}</div>
						<ul
							className={cn(styles.uploadState, {
								[styles.uploadStateError]: error,
							})}
						>
							{!error && (
								<>
									<li>{numToFixed(getProgress(upload.state) ?? 0, 0)}%</li>
									<li>|</li>
								</>
							)}
							<li>{getLabel(upload.state)}</li>
						</ul>
					</div>

					<div>
						<ButtonIcon icon="close" onClick={() => removeFile(props.ID)} />
					</div>
				</div>

				{!error && (
					<div>
						<Progress value={getProgress(upload.state)} />
					</div>
				)}
			</div>
		</div>
	);
}

function getLabel(state: Upload["state"]) {
	switch (state.status) {
		case "QUEUED":
			return "Queued for upload...";
		case "REQUESTING":
			return "Starting upload...";
		case "UPLOADING":
			return "Uploading...";
		case "CONFIRMING":
			return "Scanning file...";
		case "COMPLETE":
			return "Uploaded";
		case "ERROR":
			return state.payload.error;
	}
}

function getProgress(state: Upload["state"]) {
	switch (state.status) {
		case "QUEUED":
		case "REQUESTING":
			return undefined;
		case "UPLOADING":
			return state.payload.progress * 100;
		case "CONFIRMING":
			return undefined;
		case "COMPLETE":
			return 100;
		case "ERROR":
			return 0;
	}
}
